import { FC, useRef, useState } from 'react'
import { SectionProcessStepsProps } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import { InView } from 'react-intersection-observer'
import { Button, SectionWrapper } from '@portfolio/components'
import { ArrowNext, ArrowPrev } from '@portfolio/icons'
import { StepCard } from './stepCard'

const Container = styled('div', {
  gap: '72px',
  display: 'flex',
  flexDirection: 'column',

  '@md': {
    gap: '$30',
  },

  '@xxl': {
    maxWidth: '$maxWidthDesktop',
    margin: '0 auto',
  },
})

const Title = styled('h2', {
  projectFont: 'heading02',
  color: '$white',
  textAlign: 'center',
  paddingInline: 20,
  '@md': {
    padding: '$24 $12',
  },
  '@xxl': {
    paddingInline: 0,
  },
})

const Description = styled('p', {
  display: 'none',

  '@md': {
    display: 'flex',
    padding: '$0',
    maxWidth: '600px',
    margin: '0 auto',
    projectFont: 'heading07',
    color: '$gray300',
    textAlign: 'center',
  },
})

const ArrowButton = styled(Button, {
  display: 'none',

  '@md': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: '45%',
    transform: 'translateY(-45%)',
    zIndex: '$sliderArrows',
    width: '$56',
    height: '$56',
    borderRadius: '$rMax',
    transition: '0.2s background',

    '&:nth-of-type(1)': {
      left: '$56',
    },

    '&:nth-of-type(2)': {
      right: '$56',
    },
    background: '$gray500_02',
    backdropFilter: 'blur(10px)',
    '&:hover': {
      background: '$gray500_03',
    },
    '&:active': {
      background: '$gray500_04',
    },

    '&:focus-visible': {
      outline: '$focusBlue solid 3px;',
    },

    '&:disabled': {
      display: 'none',
    },
  },
})

const ScrollWrapper = styled('div', {
  position: 'relative',
  overflowX: 'hidden',
  [`& ${ArrowButton}`]: {
    opacity: 0,
    transition:
      'transform 0.4s ease-in, opacity 0.5s cubic-bezier(0.15, 0, 0.2, 1) 0.1s',
    transform: 'scale(0.8)',
  },
  '&:hover': {
    [`& ${ArrowButton}`]: {
      opacity: 1,
      transition:
        'transform 0.4s ease-in, opacity 0.5s cubic-bezier(0.15, 0, 0.2, 1) 0.1s',
      transform: 'scale(1)',
    },
  },
})

const StepsScrollContainer = styled('div', {
  display: 'flex',
  gap: '$20',
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollSnapType: 'x mandatory',
  padding: '$0 $20',
  scrollPadding: '$0 $20',

  '&::-webkit-scrollbar': {
    display: 'none',
  },

  '@md': {
    gap: '$30',
  },

  '@xl': {
    justifyContent: 'center',
  },
})

export const SectionProcessSteps: FC<SectionProcessStepsProps> = ({
  title,
  description,
  steps,
  spacing,
  topBorder,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isFirstVisible, setIsFirstVisible] = useState<boolean>(true)
  const [isLastVisible, setIsLastVisible] = useState<boolean>(false)

  const scrollSteps = (stepsToScroll: number) => {
    const container = scrollContainerRef.current
    if (!container) return

    const firstStep = container.firstChild as Element
    const secondStep = container.childNodes[1] as Element

    if (!firstStep || !secondStep) return

    const stepWidth = firstStep.getBoundingClientRect().width
    const gutterWidth =
      secondStep.getBoundingClientRect().left -
      firstStep.getBoundingClientRect().right
    const destination = stepsToScroll * stepWidth + stepsToScroll * gutterWidth

    container.scrollBy({
      left: destination,
      behavior: 'smooth',
    })
  }

  if (!steps || steps.length === 0) return null

  return (
    <SectionWrapper type={spacing || 'MEDIUM'} topBorder={topBorder}>
      <Container>
        {title && <Title>{title}</Title>}
        {description && <Description>{description}</Description>}

        <ScrollWrapper>
          <ArrowButton
            onClick={() => scrollSteps(-1)}
            disabled={isFirstVisible}
            aria-label="previous"
            appearance="default"
          >
            <ArrowPrev />
          </ArrowButton>

          <ArrowButton
            onClick={() => scrollSteps(1)}
            disabled={isLastVisible}
            aria-label="next"
            appearance="default"
          >
            <ArrowNext />
          </ArrowButton>

          <StepsScrollContainer ref={scrollContainerRef}>
            {steps.map((step, index) => {
              const isFirst = index === 0
              const isLast = index === steps.length - 1

              if (isFirst || isLast) {
                return (
                  <InView
                    key={step._key}
                    threshold={1}
                    root={scrollContainerRef.current}
                    rootMargin="0%"
                    onChange={(inView) => {
                      if (isFirst) {
                        setIsFirstVisible(inView)
                      }
                      if (isLast) {
                        setIsLastVisible(inView)
                      }
                    }}
                  >
                    {({ ref }) => (
                      <StepCard
                        ref={ref}
                        step={step}
                        index={index}
                        isLast={isLast}
                      />
                    )}
                  </InView>
                )
              }

              return (
                <StepCard
                  key={step._key}
                  step={step}
                  index={index}
                  isLast={isLast}
                />
              )
            })}
          </StepsScrollContainer>
        </ScrollWrapper>
      </Container>
    </SectionWrapper>
  )
}
